import { reportClientError } from '~/composables/useErrorReporter'
import { toPublicErrorMessage } from '~/utils/publicErrorMessage'
import { isBanErrorCode, redirectToBannedPage } from '~/utils/banRedirect'

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: Record<string, unknown>
  keepalive?: boolean
  timeoutMs?: number
}

class ApiError extends Error {
  status: number
  requestId: string | null
  /** Seconds until retry (429 responses from rate limit middleware). */
  retryAfter?: number
  /** Corps JSON de l’erreur (ex. conflit pension 409). */
  data?: unknown
  constructor(
    message: string,
    status: number,
    requestId: string | null = null,
    retryAfter?: number,
    data?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.requestId = requestId
    this.retryAfter = retryAfter
    this.data = data
  }
}

function createRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `api_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

function shouldReportApiError(path: string, status: number): boolean {
  // Expected for visitors on public pages: auth bootstrap checks /me and gets 401.
  if (path === '/api/auth/me' && status === 401) return false
  return status >= 500
}

async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, keepalive, timeoutMs } = options
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBase
  const requestId = createRequestId()
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer =
    controller && timeoutMs && timeoutMs > 0
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null

  let res: Response
  try {
    res = await fetch(`${apiBase}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Request-Id': requestId,
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
      keepalive,
      signal: controller?.signal,
    })
  } catch (error: any) {
    if (timer) clearTimeout(timer)
    if (error?.name === 'AbortError') {
      throw new ApiError('La requete a expire. Veuillez reessayer.', 504, requestId)
    }
    throw error
  }
  if (timer) clearTimeout(timer)
  const responseRequestId = res.headers.get('x-request-id') ?? requestId

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }))
    
    // Handle maintenance mode (503) — hard redirect to reset all SPA state
    if (res.status === 503 && err.maintenance) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('maintenance_message', err.message || 'Maintenance en cours')
      }
      if (typeof window !== 'undefined' && window.location.pathname !== '/maintenance') {
        window.location.href = '/maintenance'
      }
    }

    if (res.status === 503 && err.cooldown) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(
          'cooldown_message',
          err.message || 'Le serveur fait une courte pause. Revenez dans quelques instants.',
        )
      }
      if (typeof window !== 'undefined' && window.location.pathname !== '/cooldown') {
        window.location.href = '/cooldown'
      }
    }

    if (res.status === 403 && isBanErrorCode(err.code)) {
      redirectToBannedPage({
        code: String(err.code),
        message: typeof err.message === 'string' ? err.message : undefined,
        reason: err.reason != null ? String(err.reason) : null,
        expiresAt: err.expiresAt != null ? String(err.expiresAt) : null,
      })
    }
    
    const retryAfterRaw = err.retryAfter ?? res.headers.get('retry-after')
    const retryAfter =
      retryAfterRaw != null && retryAfterRaw !== ''
        ? Math.max(1, Math.ceil(Number(retryAfterRaw)))
        : undefined
    const rawMessage = err.message || err.errors?.[0]?.message || `API error ${res.status}`
    const apiError = new ApiError(
      toPublicErrorMessage({ message: rawMessage, status: res.status }, rawMessage),
      res.status,
      responseRequestId,
      retryAfter,
      err,
    )
    // Keep telemetry lightweight: report only actionable failures.
    if (shouldReportApiError(path, res.status)) {
      void reportClientError({
        kind: 'api_error',
        message: apiError.message,
        stack: apiError.stack,
        requestId: responseRequestId,
        meta: {
          path,
          method,
          status: res.status,
        },
      })
    }
    throw apiError
  }

  const text = await res.text()
  try {
    return JSON.parse(text) as T
  } catch {
    const parseError = new ApiError(
      'Le serveur est temporairement indisponible. Réessayez.',
      502,
      responseRequestId
    )
    void reportClientError({
      kind: 'api_error',
      message: parseError.message,
      stack: parseError.stack,
      requestId: responseRequestId,
      meta: {
        path,
        method,
        status: 502,
        parseFailure: true,
      },
    })
    throw parseError
  }
}

export function useApi() {
  return {
    get: <T = unknown>(path: string, opts?: { timeoutMs?: number }) => api<T>(path, opts),
    post: <T = unknown>(path: string, body?: Record<string, unknown>, opts?: { keepalive?: boolean }) =>
      api<T>(path, { method: 'POST', body, ...opts }),
    put: <T = unknown>(path: string, body?: Record<string, unknown>, opts?: { keepalive?: boolean }) =>
      api<T>(path, { method: 'PUT', body, ...opts }),
    patch: <T = unknown>(path: string, body?: Record<string, unknown>, opts?: { keepalive?: boolean }) =>
      api<T>(path, { method: 'PATCH', body, ...opts }),
    delete: <T = unknown>(path: string) => api<T>(path, { method: 'DELETE' }),
  }
}
